import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '../components/ui/badge'
import { SEOHead } from '@/components/seo-head'
import { buildApiUrl } from '@/config/api'
import { VENDOR_INDUSTRIES, VENDOR_INDUSTRY_SUBCATEGORIES } from '../constants/industries'
import {
  Building2,
  MapPin,
  Globe,
  Linkedin,
  Star,
  Eye,
  Search,
  ArrowRight,
  Users,
  Award,
  Sparkles
} from 'lucide-react'

interface VendorProfile {
  id: string
  companyName: string
  tagline?: string
  description: string
  industry: string
  services: string[]
  location?: string
  website?: string
  linkedinUrl?: string
  foundedYear?: string
  companySize?: string
  certifications: string[]
  languages: string[]
  keywords: string[]
  logo?: string
  coverImage?: string
  gallery: string[]
  isVerified: boolean
  isFeatured: boolean
  views: number
  likes: number
  contacts: number
  slug: string
  createdAt: string
  user: {
    id: string
    name: string
  }
  testimonials: Array<{
    id: string
    clientName: string
    clientCompany: string
    clientPosition?: string
    content: string
    rating: number
    isVerified: boolean
  }>
  portfolioItems: Array<{
    id: string
    title: string
    description: string
    category: string
    technologies: string[]
    images: string[]
    liveUrl?: string
    githubUrl?: string
    duration?: string
    budget?: number
  }>
}

interface SpotlightCategory {
  category: string
  profiles: VendorProfile[]
  count: number
}

interface SpotlightData {
  categories: SpotlightCategory[]
  totalVendors: number
}

export default function VendorSpotlightPage() {
  const [spotlightData, setSpotlightData] = useState<SpotlightData | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [categoryProfiles, setCategoryProfiles] = useState<VendorProfile[]>([])
  const [categoryLoading, setCategoryLoading] = useState(false)

  useEffect(() => {
    loadSpotlightData()
  }, [])

  useEffect(() => {
    if (selectedCategory) {
      loadCategoryProfiles(selectedCategory)
    }
  }, [selectedCategory])

  const loadSpotlightData = async () => {
    try {
      setLoading(true)
      const response = await fetch(buildApiUrl('/api/vendor-profiles/spotlight'))
      const data = await response.json()

      if (data.success) {
        setSpotlightData(data.data)
      }
    } catch (error) {
      console.error('Error loading spotlight data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCategoryProfiles = async (category: string) => {
    try {
      setCategoryLoading(true)
      const response = await fetch(
        buildApiUrl(`/api/vendor-profiles/spotlight?category=${encodeURIComponent(category)}`)
      )
      const data = await response.json()

      if (data.success) {
        setCategoryProfiles(data.data.profiles)
      }
    } catch (error) {
      console.error('Error loading category profiles:', error)
    } finally {
      setCategoryLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Redirect to vendor showcase with search term
    window.location.href = `/vendor-showcase?search=${encodeURIComponent(searchTerm)}`
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ))
  }

  const getAverageRating = (testimonials: VendorProfile['testimonials']) => {
    if (testimonials.length === 0) return 0
    const sum = testimonials.reduce((acc, t) => acc + t.rating, 0)
    return Math.round((sum / testimonials.length) * 10) / 10
  }

  const VendorCard = ({ profile, compact = false }: { profile: VendorProfile, compact?: boolean }) => (
    <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer group h-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            {profile.logo ? (
              <img
                src={profile.logo}
                alt={profile.companyName}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg group-hover:text-primary transition-colors truncate">
                {profile.companyName}
              </CardTitle>
              {profile.tagline && (
                <p className="text-sm text-gray-600 truncate">{profile.tagline}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {profile.isVerified && (
              <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                <Award className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
            {profile.isFeatured && (
              <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className={`text-gray-600 text-sm ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>
          {profile.description}
        </p>

        <div className="flex items-center text-sm text-gray-500 space-x-4">
          {profile.location && (
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-1" />
              <span className="truncate">{profile.location}</span>
            </div>
          )}
          <div className="flex items-center">
            <Eye className="w-4 h-4 mr-1" />
            {profile.views} views
          </div>
          {profile.companySize && (
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              <span className="truncate">{profile.companySize}</span>
            </div>
          )}
        </div>

        {profile.testimonials.length > 0 && (
          <div className="flex items-center">
            <div className="flex items-center mr-2">
              {renderStars(getAverageRating(profile.testimonials))}
            </div>
            <span className="text-sm text-gray-600">
              {getAverageRating(profile.testimonials)} ({profile.testimonials.length} reviews)
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-1">
          {profile.services.slice(0, compact ? 2 : 3).map((service, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {service}
            </Badge>
          ))}
          {profile.services.length > (compact ? 2 : 3) && (
            <Badge variant="outline" className="text-xs text-gray-500">
              +{profile.services.length - (compact ? 2 : 3)} more
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex space-x-3">
            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe className="w-4 h-4" />
              </a>
            )}
            {profile.linkedinUrl && (
              <a
                href={profile.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin className="w-4 h-4" />
              </a>
            )}
          </div>
          <Button 
            size="sm" 
            className="bg-primary hover:bg-primary/90"
            onClick={() => window.open(`/vendor-spotlight/${profile.slug}`, '_blank')}
          >
            View Profile
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vendor spotlight...</p>
        </div>
      </div>
    )
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Vendor Spotlight - Discover Verified Vendors & Service Providers",
    "description": "Browse verified vendors and service providers organized by industry. Find portfolios, reviews, and connect with the best business partners.",
    "url": "https://sourcedin.ai/vendor-spotlight",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Vendor Directory",
      "description": "Directory of verified business vendors and service providers",
      "numberOfItems": spotlightData?.totalVendors || 0
    }
  }

  return (
    <>
      <SEOHead
        title="Vendor Spotlight - Discover Verified Vendors & Service Providers | SourcedIn"
        description="Browse verified vendors and service providers organized by industry. Find portfolios, read reviews, and connect with the best business partners for your projects."
        keywords="vendor spotlight, verified vendors, service providers, business directory, vendor marketplace, supplier directory, professional services, business partners"
        canonical="https://sourcedin.ai/vendor-spotlight"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Vendor Spotlight
            </h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
              Discover verified vendors and service providers organized by industry. 
              Browse portfolios, read reviews, and find your perfect business partner.
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search vendors, services, or keywords..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white text-gray-900 border-0"
                  />
                </div>
                <Button type="submit" className="bg-white text-primary hover:bg-gray-100">
                  Search
                </Button>
              </div>
            </form>

            {spotlightData && (
              <div className="mt-6 text-center">
                <p className="text-lg opacity-90">
                  Featuring <span className="font-semibold">{spotlightData.totalVendors}+</span> verified vendors
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {selectedCategory ? (
          // Category View
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedCategory('')}
                  className="mb-4"
                >
                  ← Back to Categories
                </Button>
                <h2 className="text-3xl font-bold text-gray-900">
                  {selectedCategory}
                </h2>
                <p className="text-gray-600">
                  {categoryProfiles.length} vendors in this category
                </p>
              </div>
              <Button
                onClick={() => window.location.href = `/vendor-showcase?industry=${encodeURIComponent(selectedCategory)}`}
                variant="outline"
              >
                View All & Filter
              </Button>
            </div>

            {categoryLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Loading vendors...</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryProfiles.map((profile) => (
                  <VendorCard key={profile.id} profile={profile} />
                ))}
              </div>
            )}
          </div>
        ) : (
          // Categories Overview
          <div>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Browse by Industry
              </h2>
              <p className="text-xl text-gray-600">
                Find vendors organized by their expertise and industry focus
              </p>
            </div>

            {spotlightData && spotlightData.categories && spotlightData.categories.length > 0 ? (
              spotlightData.categories.map((category) => (
                <div key={category.category} className="mb-12">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {category.category}
                      </h3>
                      <p className="text-gray-600">
                        {category.count} vendors available
                      </p>
                    </div>
                    <div className="flex space-x-4">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedCategory(category.category)}
                      >
                        View All
                      </Button>
                      <Button
                        onClick={() => window.location.href = `/vendor-showcase?industry=${encodeURIComponent(category.category)}`}
                      >
                        Browse & Filter
                      </Button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {category.profiles.map((profile) => (
                      <VendorCard key={profile.id} profile={profile} compact={true} />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              // Show all industry categories even if no vendors yet
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {VENDOR_INDUSTRIES.map((industry) => (
                  <div
                    key={industry}
                    className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
                    onClick={() => setSelectedCategory(industry)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {industry}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {VENDOR_INDUSTRY_SUBCATEGORIES[industry] 
                        ? `Includes ${VENDOR_INDUSTRY_SUBCATEGORIES[industry].slice(0, 3).join(', ')}${VENDOR_INDUSTRY_SUBCATEGORIES[industry].length > 3 ? ' and more' : ''}`
                        : 'Professional services and solutions'
                      }
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        0 vendors available
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation()
                          window.location.href = `/vendor-showcase?industry=${encodeURIComponent(industry)}`
                        }}
                      >
                        Explore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CTA Section */}
            <div className="bg-white rounded-lg p-8 text-center mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Are you a vendor?
              </h3>
              <p className="text-gray-600 mb-6">
                Join our vendor spotlight and showcase your services to thousands of buyers
              </p>
              <Button
                onClick={() => window.location.href = '/vendor-profile'}
                className="bg-primary hover:bg-primary/90"
              >
                Create Your Profile
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}
